import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Search, Sun, Moon, Laptop, Globe, Check, ChevronDown } from 'lucide-react';
import { useTheme, type Theme } from '../../context/ThemeContext.js';
import { useAuth } from '../../context/AuthContext.js';
import { useI18n } from '../../context/I18nContext.js';
import { EnvironmentBadge, RoleBadge } from '../ui/Badge.js';
import { GlobalSearchModal } from './GlobalSearchModal.js';
import type { Locale } from '../../i18n/types.js';

export function Topbar() {
  const { theme, setTheme } = useTheme();
  const { admin } = useAuth();
  const { locale, setLocale, locales, currentLocaleInfo, t } = useI18n();
  const location = useLocation();
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isLangOpen, setIsLangOpen] = useState(false);
  const langMenuRef = useRef<HTMLDivElement>(null);

  const env = (import.meta.env.VITE_APP_ENV || import.meta.env.MODE || 'development').toUpperCase();

  // Close language dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (langMenuRef.current && !langMenuRef.current.contains(e.target as Node)) {
        setIsLangOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Keyboard shortcut Cmd+K or Ctrl+K for search
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsSearchOpen(true);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Compute breadcrumbs and translated page title
  const pathParts = location.pathname.split('/').filter(Boolean);
  const section = pathParts[1] || 'overview';

  const titleMap: Record<string, string> = {
    overview: t('nav.overview'),
    users: t('nav.users'),
    devices: t('nav.devices'),
    subscriptions: t('nav.subscriptions'),
    entitlements: t('nav.entitlements'),
    features: t('nav.features'),
    config: t('nav.config'),
    commands: t('nav.commands'),
    snapshots: t('nav.snapshots'),
    audit: t('nav.audit'),
    system: t('nav.system'),
  };

  const pageTitle = titleMap[section] || section.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

  return (
    <>
      <header className="h-16 border-b border-slate-200 dark:border-obsidian-750 bg-white/85 dark:bg-obsidian-950/85 backdrop-blur-md px-6 flex items-center justify-between sticky top-0 z-20">
        {/* Left: Breadcrumbs & Title */}
        <div className="flex items-center gap-4">
          <div>
            <div className="flex items-center gap-1.5 text-xs text-slate-400">
              <span>{t('topbar.admin')}</span>
              <span>/</span>
              <span className="text-slate-600 dark:text-slate-300 font-medium capitalize">
                {titleMap[section] || pathParts[1] || 'Dashboard'}
              </span>
              {pathParts[2] && (
                <>
                  <span>/</span>
                  <span className="font-mono text-indigo-600 dark:text-indigo-400 text-[11px] truncate max-w-[120px]">
                    {pathParts[2]}
                  </span>
                </>
              )}
            </div>
            <h1 className="text-lg font-bold text-slate-900 dark:text-slate-100 tracking-tight leading-tight">
              {pageTitle}
            </h1>
          </div>

          <EnvironmentBadge env={env} />
        </div>

        {/* Right: Search, Language, Theme, Role Info */}
        <div className="flex items-center gap-2.5">
          {/* Global Search Bar */}
          <button
            onClick={() => setIsSearchOpen(true)}
            className="flex items-center gap-2 px-3 py-1.5 text-xs rounded-lg border border-slate-200 dark:border-obsidian-750 bg-slate-50 dark:bg-obsidian-850 text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:border-slate-300 dark:hover:border-obsidian-700 transition-colors"
          >
            <Search className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">{t('topbar.searchPlaceholder')}</span>
            <kbd className="hidden sm:inline-block px-1.5 py-0.5 text-[10px] font-mono bg-white dark:bg-obsidian-800 border border-slate-200 dark:border-obsidian-700 rounded shadow-xs">
              {t('topbar.searchShortcut')}
            </kbd>
          </button>

          {/* Language Selector Dropdown */}
          <div className="relative" ref={langMenuRef}>
            <button
              onClick={() => setIsLangOpen(!isLangOpen)}
              className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-lg border border-slate-200 dark:border-obsidian-750 bg-slate-50 dark:bg-obsidian-850 text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-obsidian-800 transition-colors"
              title={t('topbar.language')}
              aria-label="Select Language"
            >
              <span className="text-sm leading-none">{currentLocaleInfo.flag}</span>
              <span className="font-semibold tracking-wider text-[11px]">{currentLocaleInfo.label}</span>
              <ChevronDown className="w-3 h-3 text-slate-400" />
            </button>

            {isLangOpen && (
              <div className="absolute right-0 mt-1.5 w-40 rounded-xl border border-slate-200 dark:border-obsidian-750 bg-white dark:bg-obsidian-850 shadow-xl py-1 z-50 animate-in fade-in zoom-in-95 duration-100">
                <div className="px-3 py-1.5 text-[10px] font-bold text-slate-400 uppercase tracking-wider border-b border-slate-100 dark:border-obsidian-750">
                  {t('topbar.language')}
                </div>
                {locales.map((loc) => {
                  const isActive = loc.code === locale;
                  return (
                    <button
                      key={loc.code}
                      onClick={() => {
                        setLocale(loc.code as Locale);
                        setIsLangOpen(false);
                      }}
                      className={`w-full flex items-center justify-between px-3 py-2 text-xs text-left transition-colors ${
                        isActive
                          ? 'bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400 font-semibold'
                          : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-obsidian-800'
                      }`}
                    >
                      <span className="flex items-center gap-2">
                        <span className="text-sm leading-none">{loc.flag}</span>
                        <span>{loc.nativeName}</span>
                      </span>
                      {isActive && <Check className="w-3.5 h-3.5 text-indigo-600 dark:text-indigo-400" />}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Theme Selector */}
          <div className="flex items-center rounded-lg border border-slate-200 dark:border-obsidian-750 p-0.5 bg-slate-50 dark:bg-obsidian-850">
            <button
              onClick={() => setTheme('light')}
              title={t('topbar.lightMode')}
              className={`p-1.5 rounded-md text-xs transition-colors ${
                theme === 'light'
                  ? 'bg-white dark:bg-obsidian-800 text-amber-500 shadow-xs'
                  : 'text-slate-400 hover:text-slate-600'
              }`}
            >
              <Sun className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={() => setTheme('dark')}
              title={t('topbar.darkMode')}
              className={`p-1.5 rounded-md text-xs transition-colors ${
                theme === 'dark'
                  ? 'bg-white dark:bg-obsidian-800 text-indigo-400 shadow-xs'
                  : 'text-slate-400 hover:text-slate-600'
              }`}
            >
              <Moon className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={() => setTheme('system')}
              title={t('topbar.systemTheme')}
              className={`p-1.5 rounded-md text-xs transition-colors ${
                theme === 'system'
                  ? 'bg-white dark:bg-obsidian-800 text-indigo-600 dark:text-indigo-400 shadow-xs'
                  : 'text-slate-400 hover:text-slate-600'
              }`}
            >
              <Laptop className="w-3.5 h-3.5" />
            </button>
          </div>

          {/* Admin Role Badge */}
          {admin?.role && (
            <div className="hidden md:block">
              <RoleBadge role={admin.role} />
            </div>
          )}
        </div>
      </header>

      {/* Search Modal */}
      <GlobalSearchModal isOpen={isSearchOpen} onClose={() => setIsSearchOpen(false)} />
    </>
  );
}
